<template>
  <div class="w-full flex justify-center pb-16 px-4 sm:px-8 mt-6">
    <div class="w-full max-w-6xl">
      
      <!-- LOADING STATE -->
      <div v-if="isLoading" class="mt-8">
        <div class="p-10 modern-card text-center border-dashed border-2 border-gray-200 shadow-none bg-gray-50/50 rounded-3xl transition-all duration-500">
          <div class="animate-pulse flex flex-col items-center justify-center">
            <div class="w-12 h-12 border-4 border-primary border-t-transparent rounded-full animate-spin mb-4"></div>
            <h4 class="text-primary font-semibold text-lg">Loading your authorized skill frameworks...</h4>
          </div>
        </div>
      </div>

      <!-- EMPTY STATE -->
      <div v-else-if="step === 1 && (!currentOrg || currentOrg.frameworks.length === 0)" class="mt-8 transition-all duration-500">
        <div class="p-12 modern-card text-center border-dashed border-2 border-red-100 shadow-none bg-red-50/30 rounded-3xl">
          <div class="mx-auto my-6 flex justify-center items-center rounded-full bg-red-100 text-red-500 shadow-sm" style="width: 80px; height: 80px;">
            <svg xmlns="http://www.w3.org/2000/svg" width="36" height="36" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><circle cx="12" cy="12" r="10"></circle><line x1="12" y1="8" x2="12" y2="12"></line><line x1="12" y1="16" x2="12.01" y2="16"></line></svg>
          </div>
          <h3 class="mb-3 font-extrabold text-3xl text-red-600">No Frameworks Available</h3>
          <p class="text-gray-500 text-lg max-w-md mx-auto">You have not been assigned to any Coach Skill Frameworks in this active organization.</p>
        </div>
      </div>

      <!-- ========================================== -->
      <!-- STEP 1: SELECT FRAMEWORK                   -->
      <!-- ========================================== -->
      <div v-else-if="step === 1" class="step-1-container animate-fade-in">
        
        <!-- Header (Strictly Left Aligned) -->
        <div class="flex flex-col mb-12 pb-6 border-b border-gray-100 text-left w-full items-start">
          <h1 class="text-4xl font-extrabold text-gray-900 tracking-tight mb-3">
            Select Skill Framework
          </h1>
          <p class="text-gray-500 font-medium text-lg">Click on a framework below to view and select its skills.</p>
        </div>

        <!-- Dynamic Grid (Larger cards, max 3 per row) -->
        <div class="flex flex-wrap items-stretch -mx-4" v-if="currentOrg">
          <div 
            class="p-4 w-full sm:w-1/2 lg:w-1/3 flex" 
            v-for="item in currentOrg.frameworks" 
            :key="item.value"
          >
            <div @click="chooseFramework(item.value)" class="w-full flex h-full">
              <!-- VERTICAL CARD (Left aligned, no circles, forced border-radius) -->
              <div class="framework-vertical-card bg-white p-8 sm:p-10 border border-gray-100 flex flex-col items-start text-left group w-full relative overflow-hidden">
                
                <!-- Icon -->
                <div class="fw-card-icon-wrapper mb-8 relative z-10">
                  <span class="inline-flex items-center justify-center w-20 h-20 bg-primary/10 text-primary shadow-sm" style="border-radius: 1rem;">
                    <i :class="'feather ' + item.icon" class="text-4xl"></i>
                  </span>
                </div>
                
                <!-- Text Content -->
                <div class="relative z-10 flex-grow flex flex-col w-full">
                  <h4 class="mb-4 font-extrabold text-gray-800 text-2xl leading-tight card-title">
                    {{ item.text }}
                  </h4>
                  <p class="text-gray-500 text-base font-medium leading-relaxed m-0 flex-grow">
                    {{ item.desc }}
                  </p>
                </div>
                
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- ========================================== -->
      <!-- STEP 2: VIEW CHARACTERISTICS DATA          -->
      <!-- ========================================== -->
      <div v-if="step === 2" class="step-2-container animate-fade-in">
        
        <!-- Header (Strictly Left Aligned) -->
        <div class="flex flex-col mb-10 pb-6 border-b border-gray-100 text-left w-full items-start">
          <div class="mb-6">
            <vs-button type="flat" class="text-gray-500 hover:text-primary font-semibold -ml-4 transition-colors duration-300" icon-pack="feather" icon="icon-arrow-left" @click="goBackToFrameworks">
              Back to Frameworks
            </vs-button>
          </div>
          <h1 class="text-3xl sm:text-4xl font-extrabold text-gray-900 tracking-tight mb-3">
            {{ $t("selectedCharacteristic.sc1") }} - <span class="text-primary">{{ getFrameworkName }}</span>
          </h1>
          <p class="text-gray-500 font-medium text-lg">{{ $t("selectedCharacteristic.sc2") }}</p>
        </div>

        <!-- Global Search Bar -->
        <div class="mb-10 w-full">
          <div @click="searchCompetency" class="bg-white border border-gray-100 shadow-sm cursor-pointer overflow-hidden search-card group framework-vertical-card">
            <div class="p-6 flex items-center justify-between">
              <div class="flex items-center">
                <span class="p-4 inline-flex feather-icon select-none relative text-primary mr-6 bg-primary/10 search-icon-wrapper" style="border-radius: 1rem;">
                  <svg xmlns="http://www.w3.org/2000/svg" width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round"><circle cx="11" cy="11" r="8"></circle><line x1="21" y1="21" x2="16.65" y2="16.65"></line></svg>
                </span>
                <div class="text-left">
                  <h4 class="mb-2 font-extrabold text-gray-800 text-xl">{{ $t("selectedCharacteristic.sc4") }}</h4>
                  <p class="text-sm text-gray-400 font-semibold uppercase tracking-wider">Search across all Steps of Your Journey</p>
                </div>
              </div>
              <div class="opacity-0 group-hover:opacity-100 text-gray-400 pr-4 arrow-slide">
                <svg xmlns="http://www.w3.org/2000/svg" width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><polyline points="9 18 15 12 9 6"></polyline></svg>
              </div>
            </div>
          </div>
        </div>

        <!-- Data Grid -->
        <div class="vx-row" v-for="(row, rowIndex) in allCharacteristics" :key="'row-' + rowIndex">
          <div class="vx-col w-1/3 sm:w-1/3 lg:w-1/3 mb-8 px-4" v-for="chrt in row" :key="'col-' + selectedFramework + '-' + chrt.id">
            
            <div 
              class="bg-white border border-gray-100 h-full flex flex-col items-center justify-center p-8 text-center cursor-pointer select-none relative overflow-hidden group step-2-card"
              :class="{ 'is-selected': selectedCharacteristic === chrt.id }" 
              @click="showCharacteristicDetail(rowIndex, chrt.id)"
              @dblclick.stop="doubleClickCharacteristic(rowIndex, chrt.id)"
            >
              <!-- Selected State Background -->
              <div class="absolute inset-0 bg-primary bg-fade-layer" :class="selectedCharacteristic === chrt.id ? 'opacity-100' : 'opacity-0'"></div>

              <div class="relative z-10 w-full flex flex-col items-center">
                <span class="p-5 mb-5 inline-flex char-icon-wrapper" style="border-radius: 50%;" :class="selectedCharacteristic === chrt.id ? 'bg-white/20 scale-105' : 'bg-gray-50'">
                  <img :src="chrt.icon" alt="chrt" width="56px" height="56px" :class="{'brightness-0 invert': selectedCharacteristic === chrt.id}" class="icon-filter" />
                </span>
                
                <h4 class="font-bold text-lg leading-snug w-full title-color" :class="selectedCharacteristic === chrt.id ? 'text-white' : 'text-gray-700'">
                  {{ chrt.characteristic }}
                </h4>
              </div>

              <!-- Selected Checkmark -->
              <div v-if="selectedCharacteristic === chrt.id" class="absolute top-5 right-5 text-white animate-fade-in">
                <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="3" stroke-linecap="round" stroke-linejoin="round"><polyline points="20 6 9 17 4 12"></polyline></svg>
              </div>
            </div>

          </div>

          <!-- Detail Dropdown View -->
          <div class="vx-col w-full mb-12 px-4" v-if="characteristicDetail === rowIndex">
  <div
    class="bg-gray-50 border border-gray-200 p-8 sm:p-10 shadow-inner relative overflow-hidden animate-fade-in-down text-left"
    style="border-radius: 1.5rem;"
  >
    <div class="absolute top-0 left-1/2 transform -translate-x-1/2 -mt-4 w-8 h-8 bg-gray-50 border-t border-l border-gray-200 rotate-45"></div>

    <h4 class="font-extrabold text-gray-800 text-2xl mb-4 relative z-10 text-left">
      Characteristic Details
    </h4>

    <p class="mb-8 text-gray-600 text-lg leading-relaxed font-medium relative z-10 text-left">
      {{ characteristicDef }}
    </p>

    <div class="flex justify-start relative z-10">
      <vs-button
        class="w-full sm:w-1/2 lg:w-1/3 shadow-lg hover-lift font-bold text-lg py-4"
        style="border-radius: 1rem;"
        color="primary"
        type="filled"
        @click="storeCharacteristics()"
      >
        {{ $t("selectedCharacteristic.sc3") }}
      </vs-button>
    </div>
  </div>
</div>
        </div>

      </div>
    </div>
  </div>
</template>

<script>
import baseData from "@/i18n/i18n-en-Data-DPM.js";           
import deepData from "@/i18n/i18n-en-deep-Data.js";       
import pciData from "@/i18n/i18n-en-pci-Data-Coach.js";         
import peopletreeData from "@/i18n/i18n-en-peopletree-Data.js"; 

export default {
  name: "SelectCharacteristics",
  data() {
    return {
      isLoading: false,
      step: 1, 
      selectedFramework: null, 
      
      masterFrameworkOptions: [
        { text: "Behaviour & Leadership", value: "behavioural", desc: "Improve communication, leadership habits, and values-based behavior across your team.", icon: "icon-users" },
        { text: "Founder Journey", value: "founder", desc: "Support founders and startup teams through product, market, and growth stages.", icon: "icon-award" },
        { text: "Performance & Growth", value: "performance", desc: "Track goals, KPIs, and progress for stronger performance and career development.", icon: "icon-trending-up" },
        { text: "Ecosystem & Community", value: "ecosystem", desc: "Build stronger communities, networks, and partnerships around shared goals.", icon: "icon-globe" },
      ],
      
      allCharacteristics: [],
      selectedCharacteristic: null,
      selected: [],
      CCD: {}, 
      characteristicDetail: null,
      characteristicDef: null,
      
      selectedCharId: (this.$store.state.addSkill && this.$store.state.addSkill.characteristic) 
                      ? this.$store.state.addSkill.characteristic.id 
                      : null
    };
  },
  computed: {
    currentOrg() {
      const activeOrgId = this.$store.state.activeCoachOrgId;
      const allOrgs = this.$store.state.userCoachOrgs;
      if (!activeOrgId || !allOrgs) return null;

      const orgData = allOrgs.find(org => org.org_id === activeOrgId);
      if (!orgData) return null;

      return {
        org_id: orgData.org_id,
        org_name: orgData.org_name,
        frameworks: this.masterFrameworkOptions.filter(f => orgData.frameworks.includes(f.value))
      };
    },
    getFrameworkName() {
      const found = this.masterFrameworkOptions.find(opt => opt.value === this.selectedFramework);
      return found ? found.text : "Framework";
    }
  },
  mounted() {
    const storedCharacteristic = this.$store.state.addSkill ? this.$store.state.addSkill.characteristic : null;
    if (storedCharacteristic && storedCharacteristic.id) {
      this.selectedCharacteristic = storedCharacteristic.id;
    }
  },
  created() {
    this.loadCompetencies();
  },
  methods: {
    loadCompetencies() {
      const storedChar = this.$store.state.addSkill.characteristic;
      if (!storedChar) {
         this.competencies = [];
         return;
      }
      if (storedChar.competencies && storedChar.competencies.length > 0) {
         this.competencies = storedChar.competencies;
         return;
      }
      let CCD = {};
      switch(storedChar.framework) {
        case 'founder':     CCD = pciData.addNewSkill.data; break;
        case 'behavioural': CCD = baseData.addNewSkill.data; break;
        case 'ecosystem':   CCD = deepData.addNewSkill.data; break;
        case 'performance': CCD = peopletreeData.addNewSkill.data; break;
        default:            CCD = pciData.addNewSkill.data;
      }
      if (CCD[storedChar.id]) {
        this.competencies = CCD[storedChar.id].competencies;
      } else {
        this.competencies = [];
      }
    },
    doubleClickCharacteristic(rowIndex, id) {
      this.showCharacteristicDetail(rowIndex, id);
      this.storeCharacteristics();
    },
    chooseFramework(value) {
      if (value === 'sales') {
        this.$vs.notify({title: 'Coming Soon', text: ' Coming soon.', color: 'warning', iconPack: 'feather', icon: 'icon-alert-circle'});
        return; 
      }
      this.selectedFramework = value;
      this.step = 2;
      this.selectedCharacteristic = null;
      this.characteristicDetail = null;
      this.loadDataFromFile();

      let selectedDataFile = null;
      switch(value) {
        case 'founder':     selectedDataFile = pciData; break;
        case 'behavioural': selectedDataFile = baseData; break;
        case 'ecosystem':   selectedDataFile = deepData; break;
        case 'performance': selectedDataFile = peopletreeData; break;
        default:            selectedDataFile = pciData;
      }

      if (selectedDataFile && this.$i18n) {
        const locale = this.$i18n.locale || 'en';
        this.$i18n.mergeLocaleMessage(locale, selectedDataFile);
      }
      if (this.$parent && selectedDataFile && selectedDataFile.addNewSkill) {
        this.$parent.CCD = selectedDataFile.addNewSkill.data;
      }
    },
    searchCompetency() {
      this.$store.dispatch("addSkill/searchCompetency", true).catch(err => { console.log("error search competency", err); });
    },
    goBackToFrameworks() {
      this.step = 1; 
      this.selectedFramework = null;
      this.allCharacteristics = [];
    },
    loadDataFromFile() {
      switch(this.selectedFramework) {
        case 'founder': this.CCD = pciData.addNewSkill.data; break;
        case 'behavioural': this.CCD = baseData.addNewSkill.data; break;
        case 'ecosystem':   this.CCD = deepData.addNewSkill.data; break;
        case 'performance': this.CCD = peopletreeData.addNewSkill.data; break;
        default:            this.CCD = pciData.addNewSkill.data;
      }
      this.getAllCharacteristics();
    },
    emitToParent(event) {
      this.$emit("childToParent", this.childMessage);
    },
    getAllCharacteristics() {
      const allChar = [];
      let currentRow = [];
      for (let ccd in this.CCD) {
        currentRow.push({
          id: ccd,
          icon: this.CCD[ccd].icon,
          characteristic: this.CCD[ccd].characteristic, 
          wdd: this.CCD[ccd].wdd,
          competencies: this.CCD[ccd].competencies
        });
        if (currentRow.length === 3) {
          allChar.push(currentRow);
          currentRow = [];
        }
      }
      if (currentRow.length > 0) { allChar.push(currentRow); }
      this.allCharacteristics = allChar;
    },
    showCharacteristicDetail(rowIndex, id) {
      this.characteristicDetail = rowIndex;
      this.selectedCharacteristic = id;
      this.characteristicDef = this.CCD[id].wdd;
      this.selectedCharId = id;
    },
    storeCharacteristics() {
      const selectedData = this.CCD[this.selectedCharId];
      const payload = {
        id: this.selectedCharId,
        framework: this.selectedFramework,
        org_id: this.$store.state.activeCoachOrgId,
        ...selectedData
      };

      this.$store
        .dispatch("addSkill/addSelectedCharacteristic", payload)
        .then(res => { this.emitToParent(); })
        .catch(err => { console.log("could not add selected characteristics", err); });
    }
  }
};
</script>

<style scoped>
/* Base Animations */
.animate-fade-in {
  animation: fadeIn 0.4s ease-out forwards;
}
.animate-fade-in-down {
  animation: fadeInDown 0.4s cubic-bezier(0.175, 0.885, 0.32, 1.275) forwards;
}

@keyframes fadeIn {
  0% { opacity: 0; }
  100% { opacity: 1; }
}
@keyframes fadeInDown {
  0% { opacity: 0; transform: translateY(-10px); }
  100% { opacity: 1; transform: translateY(0); }
}

.hover-lift {
  transition: transform 0.3s cubic-bezier(0.25, 1, 0.5, 1), box-shadow 0.3s cubic-bezier(0.25, 1, 0.5, 1);
}
.hover-lift:hover {
  transform: translateY(-2px);
  box-shadow: 0 10px 25px -5px rgba(var(--vs-primary), 0.4) !important;
}

/* ------------------------------------- 
   VERTICAL FRAMEWORK CARDS
   ------------------------------------- */
.framework-vertical-card { 
  cursor: pointer; 
  border-radius: 1.5rem !important; /* Explicitly forcing rounded corners */
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.03); 
  transition: all 0.5s cubic-bezier(0.25, 1, 0.5, 1); /* Buttery smooth ease-out */
}
.framework-vertical-card:hover { 
  transform: translateY(-6px); 
  border-color: rgba(var(--vs-primary), 0.4); 
  box-shadow: 0 15px 35px -10px rgba(var(--vs-primary), 0.15); 
}

/* Icon slide effect on hover */
.fw-card-icon-wrapper {
  transition: transform 0.5s cubic-bezier(0.25, 1, 0.5, 1);
}
.framework-vertical-card:hover .fw-card-icon-wrapper {
  transform: translateX(6px);
}

/* Title color transition */
.card-title {
  transition: color 0.4s ease;
}
.framework-vertical-card:hover .card-title {
  color: rgba(var(--vs-primary), 1);
}

/* Search bar specific animations */
.search-icon-wrapper {
  transition: all 0.5s cubic-bezier(0.25, 1, 0.5, 1);
}
.search-card:hover .search-icon-wrapper {
  background: rgba(var(--vs-primary), 0.15);
  transform: scale(1.05);
}
.arrow-slide {
  transition: all 0.5s cubic-bezier(0.25, 1, 0.5, 1);
  transform: translateX(-10px);
}
.search-card:hover .arrow-slide {
  opacity: 1;
  transform: translateX(0);
}

/* ------------------------------------- 
   STEP 2: CHARACTERISTIC CARDS
   ------------------------------------- */
.step-2-card {
  border-radius: 1.5rem !important; /* Forced rounding */
  box-shadow: 0 4px 15px rgba(0, 0, 0, 0.02);
  transition: all 0.5s cubic-bezier(0.25, 1, 0.5, 1);
}
.step-2-card:hover:not(.is-selected) {
  transform: translateY(-5px);
  border-color: rgba(var(--vs-primary), 0.3);
  box-shadow: 0 12px 25px -8px rgba(0, 0, 0, 0.1);
}
.is-selected {
  transform: translateY(-5px);
  box-shadow: 0 15px 35px -10px rgba(var(--vs-primary), 0.4);
  border-color: rgba(var(--vs-primary), 1);
}

.char-icon-wrapper {
  transition: all 0.5s cubic-bezier(0.25, 1, 0.5, 1);
}
.step-2-card:hover:not(.is-selected) .char-icon-wrapper {
  background: rgba(var(--vs-primary), 0.1) !important;
  transform: scale(1.1);
}

.bg-fade-layer, .icon-filter, .title-color {
  transition: all 0.4s ease;
}

/* Image filtering trick to turn icons white when selected */
.brightness-0 { filter: brightness(0); }
.invert { filter: brightness(0) invert(1); }
</style>